import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { useContext } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { WorkflowRuleFullPage } from "@/components/Organisms/WorkflowRuleFullPage/WorkflowRuleFullPage";
import { EMPTY_ORG_ID } from "@/constants/org";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import { TicketTypeDrawerContext } from "@/utils/ticket-type-detail/ticket-type-drawer-context";

const SearchSchema = z.object({
	ruleTab: z.enum(["details", "activity"]).optional().default("details"),
});

const ROUTE_ID =
	"/_authenticated/(admin-app)/_admin/admin/configuration/ticketTypes/$ticketTypeId/full/rules/$ruleId" as const;

export const Route = createFileRoute(ROUTE_ID)({
	component: AdminTicketTypeRuleDetailPage,
	validateSearch: (search) => SearchSchema.parse(search),
});

function AdminTicketTypeRuleDetailPage() {
	const ctx = useContext(TicketTypeDrawerContext);
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({ from: ROUTE_ID });
	const search = useSearch({ from: ROUTE_ID });

	const canEditRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "EditWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canEditRule = Boolean(canEditRuleQuery.data);

	const canDeleteRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "DeleteWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canDeleteRule = Boolean(canDeleteRuleQuery.data);

	const canArchiveRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ArchiveWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canArchiveRule = Boolean(canArchiveRuleQuery.data);

	if (!ctx) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<LoadingIndicator type="line-simple" size="xl" supportingText />
			</div>
		);
	}

	const { ticketType } = ctx;

	return (
		<WorkflowRuleFullPage
			ruleId={params.ruleId}
			organizationId={EMPTY_ORG_ID}
			lifecycleId={ticketType.lifecycleId}
			entityTypeId={params.ticketTypeId}
			canEdit={canEditRule}
			canDelete={canDeleteRule}
			canArchive={canArchiveRule}
			entityTypeLabel={t("admin.ticketTypes.table.title")}
			entityTypeName={ticketType.name ?? ""}
			activeTab={search.ruleTab}
			onTabChange={(ruleTab) => {
				void navigate({
					to: "/admin/configuration/ticketTypes/$ticketTypeId/full/rules/$ruleId",
					params: { ticketTypeId: params.ticketTypeId, ruleId: params.ruleId },
					search: { tab: "rules", mode: "view", presentation: "full", ruleTab },
				});
			}}
			onBackToRules={() => {
				void navigate({
					to: "/admin/configuration/ticketTypes/$ticketTypeId/full",
					params: { ticketTypeId: params.ticketTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onNavigateToEntityTypeList={() => {
				void navigate({ to: "/admin/configuration/ticketTypes" });
			}}
			onNavigateToEntityTypeRules={() => {
				void navigate({
					to: "/admin/configuration/ticketTypes/$ticketTypeId/full",
					params: { ticketTypeId: params.ticketTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onAfterAction={() => {
				void navigate({
					to: "/admin/configuration/ticketTypes/$ticketTypeId/full",
					params: { ticketTypeId: params.ticketTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
		/>
	);
}
